import { apiService } from './api';
import { Workflow, Node, Connection, Execution, WorkflowSchedule, WorkflowVersion, WorkflowCredential } from '../types/workflow.types';
import { FilterParams, PaginatedResponse } from '../types/api.types';

export const workflowService = {
  // Workflow management
  getWorkflows: async (params?: FilterParams): Promise<PaginatedResponse<Workflow>> => {
    return await apiService.get<PaginatedResponse<Workflow>>('/workflows/', params);
  },

  getWorkflow: async (id: string): Promise<Workflow> => {
    return await apiService.get<Workflow>(`/workflows/${id}/`);
  },

  createWorkflow: async (data: Partial<Workflow>): Promise<Workflow> => {
    return await apiService.post<Workflow>('/workflows/', data);
  },

  updateWorkflow: async (id: string, data: Partial<Workflow>): Promise<Workflow> => {
    return await apiService.patch<Workflow>(`/workflows/${id}/`, data);
  },

  deleteWorkflow: async (id: string): Promise<void> => {
    await apiService.delete(`/workflows/${id}/`);
  },

  // Workflow execution
  executeWorkflow: async (
    id: string,
    trigger_data?: any
  ): Promise<{ execution_id: string; status: string }> => {
    return await apiService.post(`/workflows/${id}/execute/`, trigger_data ? { trigger_data } : {});
  },

  executeChatWorkflow: async (
    id: string,
    chatInput: string,
    sessionId?: string
  ): Promise<{ execution_id: string; status: string; sessionId?: string }> => {
    return await apiService.post(`/workflows/${id}/chat/`, {
      chatInput,
      sessionId,
    });
  },

  // Workflow import/export
  exportWorkflow: async (id: string): Promise<any> => {
    return await apiService.get(`/workflows/${id}/export/`);
  },

  importWorkflow: async (data: any): Promise<Workflow> => {
    // n8n exports can be large (dozens of nodes / connections). Bump the
    // per-call timeout to 5 minutes so big imports don't bail at the global
    // 30s default while the backend is still wiring nodes + connections.
    return await apiService.post('/workflows/import_workflow/', data, {
      timeout: 5 * 60 * 1000,
    });
  },

  duplicateWorkflow: async (id: string): Promise<Workflow> => {
    return await apiService.post(`/workflows/${id}/duplicate/`);
  },

  validateWorkflow: async (id: string): Promise<{ valid: boolean; message: string }> => {
    return await apiService.post(`/workflows/${id}/validate_workflow/`);
  },

  // Node management
  getNodes: async (workflowId: string): Promise<Node[]> => {
    return await apiService.get<Node[]>(`/workflows/${workflowId}/nodes/`);
  },

  createNode: async (workflowId: string, data: Partial<Node>): Promise<Node> => {
    return await apiService.post<Node>(`/workflows/${workflowId}/nodes/`, data);
  },

  updateNode: async (workflowId: string, nodeId: string, data: Partial<Node>): Promise<Node> => {
    return await apiService.patch<Node>(`/workflows/${workflowId}/nodes/${nodeId}/`, data);
  },

  deleteNode: async (workflowId: string, nodeId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/nodes/${nodeId}/`);
  },

  // Connection management
  getConnections: async (workflowId: string): Promise<Connection[]> => {
    return await apiService.get<Connection[]>(`/workflows/${workflowId}/connections/`);
  },

  createConnection: async (workflowId: string, data: Partial<Connection>): Promise<Connection> => {
    return await apiService.post<Connection>(`/workflows/${workflowId}/connections/`, data);
  },

  updateConnection: async (workflowId: string, connectionId: string, data: Partial<Connection>): Promise<Connection> => {
    return await apiService.patch<Connection>(`/workflows/${workflowId}/connections/${connectionId}/`, data);
  },

  deleteConnection: async (workflowId: string, connectionId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/connections/${connectionId}/`);
  },

  // Execution management
  getExecutions: async (params?: FilterParams): Promise<PaginatedResponse<Execution>> => {
    return await apiService.get<PaginatedResponse<Execution>>('/executions/', params);
  },

  getExecution: async (id: string): Promise<Execution> => {
    return await apiService.get<Execution>(`/executions/${id}/`);
  },

  getWorkflowExecutions: async (workflowId: string, params?: FilterParams): Promise<PaginatedResponse<Execution>> => {
    return await apiService.get<PaginatedResponse<Execution>>(`/workflows/${workflowId}/executions/`, params);
  },

  // Schedule management
  getSchedules: async (workflowId: string): Promise<WorkflowSchedule[]> => {
    return await apiService.get<WorkflowSchedule[]>(`/workflows/${workflowId}/schedules/`);
  },

  createSchedule: async (workflowId: string, data: Partial<WorkflowSchedule>): Promise<WorkflowSchedule> => {
    return await apiService.post<WorkflowSchedule>(`/workflows/${workflowId}/schedules/`, data);
  },

  updateSchedule: async (workflowId: string, scheduleId: string, data: Partial<WorkflowSchedule>): Promise<WorkflowSchedule> => {
    return await apiService.patch<WorkflowSchedule>(`/workflows/${workflowId}/schedules/${scheduleId}/`, data);
  },

  deleteSchedule: async (workflowId: string, scheduleId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/schedules/${scheduleId}/`);
  },

  // Workflow credentials (reusable per workflow)
  getCredentials: async (workflowId: string): Promise<WorkflowCredential[]> => {
    return await apiService.get<WorkflowCredential[]>(`/workflows/${workflowId}/credentials/`);
  },

  createCredential: async (
    workflowId: string,
    data: Partial<WorkflowCredential>
  ): Promise<WorkflowCredential> => {
    return await apiService.post<WorkflowCredential>(`/workflows/${workflowId}/credentials/`, data);
  },

  updateCredential: async (
    workflowId: string,
    credentialId: string,
    data: Partial<WorkflowCredential>
  ): Promise<WorkflowCredential> => {
    return await apiService.patch<WorkflowCredential>(`/workflows/${workflowId}/credentials/${credentialId}/`, data);
  },

  deleteCredential: async (workflowId: string, credentialId: string): Promise<void> => {
    await apiService.delete(`/workflows/${workflowId}/credentials/${credentialId}/`);
  },

  // Odoo helpers -- the Node Inspector calls these to populate the Model
  // dropdown and Fields multi-select once the user picks a credential.
  listOdooModels: async (
    workflowId: string,
    credentialId: string,
    search?: string,
  ): Promise<{ name: string; label: string }[]> => {
    const resp = await apiService.post<{ models: { name: string; label: string }[] }>(
      `/workflows/${workflowId}/credentials/${credentialId}/odoo-models/`,
      { search: search ?? '' },
    );
    return resp?.models || [];
  },

  listOdooFields: async (
    workflowId: string,
    credentialId: string,
    model: string,
  ): Promise<
    {
      name: string;
      label: string;
      type: string;
      required: boolean;
      readonly?: boolean;
      relation?: string;
      selection?: [string, string][];
      help?: string;
    }[]
  > => {
    const resp = await apiService.post<{ fields: any[] }>(
      `/workflows/${workflowId}/credentials/${credentialId}/odoo-fields/`,
      { model },
    );
    return resp?.fields || [];
  },

  // Version management
  getVersions: async (workflowId: string): Promise<WorkflowVersion[]> => {
    return await apiService.get<WorkflowVersion[]>(`/workflows/${workflowId}/versions/`);
  },

  createVersion: async (workflowId: string, data: { name: string; description?: string }): Promise<WorkflowVersion> => {
    return await apiService.post<WorkflowVersion>(`/workflows/${workflowId}/create_version/`, data);
  },

  // AI-powered workflow editing
  aiEdit: async (
    workflowId: string,
    payload: { prompt: string; nodes: any[]; connections: any[] }
  ): Promise<{
    explanation: string;
    add_nodes: any[];
    add_connections: any[];
    remove_nodes: string[];
    update_nodes: any[];
  }> => {
    return await apiService.post(`/workflows/${workflowId}/ai-edit/`, payload);
  },

  // AI-powered workflow generation from prompt
  aiGenerate: async (
    payload: { prompt: string }
  ): Promise<{ workflow_id: string; explanation: string }> => {
    return await apiService.post('/workflows/ai-generate/', payload);
  },

  // Templates
  getTemplates: async (): Promise<any[]> => {
    return await apiService.get('/workflows/templates/');
  },
};